import React, { useState, useEffect } from 'react';
import {
    Brain,
    Sparkles,
    Zap,
    Shield,
    TrendingUp,
    Users,
    Globe,
    Award,
    ChevronRight,
    CircuitBoard,
    Lightbulb,
    Target,
    Search,
    Code,
    Rocket,
    Database,
    Lock,
    CheckCircle2,
    ArrowRight,
    Star,
    Building2,
    FileText,
    Play
} from 'lucide-react';

const AIServiceLayout = ({ service }) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    const heroSlides = [
        {
            title: 'TechSPR GenAI Hub',
            subtitle: 'Create Generative AI experiences quickly, efficiently, and responsibly',
            image: '/images/ai/slider1.jfif'
        },
        {
            title: 'Enterprise Data & AI Readiness',
            subtitle: 'Re(AI)magine your Data & AI Ecosystem for Tomorrow',
            image: '/images/ai/uc1.jfif'
        },
        {
            title: 'Agentic AI for Enterprise',
            subtitle: 'Faster outcomes, smarter decisions, and sustainable growth powered by AI',
            image: '/images/ai/uc2.jfif'
        },
        {
            title: service.name,
            subtitle: service.description,
            image: '/images/ai/uc3.jfif'
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [heroSlides.length]);

    return (
        <div className="bg-[#0f0f10] text-white min-h-screen">
            {/* Hero Carousel */}
            <section className="relative h-[600px] overflow-hidden">
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                            }`}
                        style={{
                            backgroundImage: `url(${slide.image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    >
                        {/* Gradient Overlay */}
                        <div
                            className="absolute inset-0"
                            style={{
                                background: 'linear-gradient(135deg, rgba(26, 11, 46, 0.95) 0%, rgba(22, 33, 62, 0.9) 50%, rgba(15, 15, 16, 0.95) 100%)'
                            }}
                        />

                        <div className="relative z-10 h-full flex items-center px-6">
                            <div className="max-w-7xl mx-auto w-full">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="p-3 bg-purple-600/20 rounded-xl backdrop-blur-sm border border-purple-500/30">
                                        <Brain className="w-12 h-12 text-purple-400" strokeWidth={1.5} />
                                    </div>
                                </div>
                                <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-purple-200 to-purple-400 bg-clip-text text-transparent max-w-4xl">
                                    {slide.title}
                                </h1>
                                <p className="text-xl text-gray-300 max-w-3xl mb-8 leading-relaxed">
                                    {slide.subtitle}
                                </p>
                                <button
                                    className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all duration-300 flex items-center gap-2 shadow-lg shadow-purple-500/30"
                                    data-testid="hero-cta-btn"
                                >
                                    Learn More
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Slide Indicators */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-12 bg-purple-500' : 'w-2 bg-white/30'
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>

                {/* Scroll Indicator */}
                {/* <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-center z-20">
                    <div className="text-sm text-gray-400 mb-2">Latest Insights</div>
                    <ChevronRight className="w-5 h-5 text-purple-400 mx-auto rotate-90 animate-bounce" />
                </div> */}
            </section>

            {/* Mission Statement */}
            <section className="py-20 px-6 bg-[#0f0f10]">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center">
                        <span className="bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent">
                            Re(AI)magining the World
                        </span>
                    </h2>
                    <p className="text-lg text-gray-300 leading-relaxed text-center max-w-5xl mx-auto">
                        is not a campaign or a slogan — it&apos;s our commitment to reimagine the limitless potential of AI.
                        Within global enterprises, AI is powering new waves of imaginative thinking about how to change, grow,
                        and thrive in their industries. We invite you to explore how we can reimagine your AI journey together.
                    </p>
                </div>
            </section>

            {/* Our AI Offerings */}
            <section className="py-20 px-6 bg-gradient-to-b from-[#0f0f10] to-[#1a1a2e]" data-testid="offerings-section">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 text-white">Our AI Offerings</h2>
                        <p className="text-gray-400 text-lg">We design AI services from the ground-up to drive growth and transformation</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                icon: Building2,
                                title: 'Industry Solutions',
                                description: 'AI-inspired ideas are driving innovation across industries. We collaborate with clients to create purpose-built applications.',
                                image: '/images/ai/img1.jfif'
                            },
                            {
                                icon: Rocket,
                                title: 'Enterprise Reinvention',
                                description: 'Generative AI has potential to transform businesses. We co-create solutions that drive productivity and growth.',
                                image: '/images/ai/img2.jfif'
                            },
                            {
                                icon: Search,
                                title: 'Search and Insights',
                                description: 'Our AI-enhanced conversational interfaces produce original content in response to queries, leveraging unique business styles.',
                                image: '/images/ai/img3.jfif'
                            },
                            {
                                icon: Code,
                                title: 'Accelerated Engineering',
                                description: 'Our teams leverage custom AI-Copilot solutions to optimize digital engineering and build modern applications efficiently.',
                                image: '/images/ai/img4.jpeg'
                            }
                        ].map((offering, i) => {
                            const Icon = offering.icon;
                            return (
                                <div
                                    key={i}
                                    className="group bg-white/5 hover:bg-white/10 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300"
                                    data-testid={`offering-card-${i}`}
                                >
                                    <div
                                        className="h-48 bg-cover bg-center relative"
                                        style={{ backgroundImage: `url(${offering.image})` }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e] via-[#1a1a2e]/80 to-transparent" />
                                        <div className="absolute bottom-4 left-4 p-3 bg-purple-600/30 backdrop-blur-sm rounded-lg border border-purple-500/30">
                                            <Icon className="text-purple-300 w-6 h-6" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-bold text-xl mb-3 text-white group-hover:text-purple-300 transition-colors">
                                            {offering.title}
                                        </h3>
                                        <p className="text-gray-400 leading-relaxed text-sm">
                                            {offering.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Band - Report Download */}
            <section
                className="py-16 px-6 relative overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)'
                }}
            >
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="flex-1">
                            <h2 className="text-3xl font-bold mb-3 text-white">
                                AI-Powered CX Strategies for Industries
                            </h2>
                            <p className="text-purple-100 text-lg">
                                Learn How 300 Global Enterprises are Modernizing Customer Experience (CX)
                            </p>
                        </div>
                        <div className="flex gap-4">
                            <button className="px-8 py-4 bg-white text-purple-600 hover:bg-gray-100 font-semibold rounded-lg transition-all duration-300 shadow-lg">
                                Download the Report
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Industry Use Cases */}
            <section className="py-20 px-6 bg-[#1a1a2e]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 text-white">Explore Our Industry Use Cases</h2>
                        <p className="text-gray-400 text-lg">Proven AI implementations delivering measurable results</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: TrendingUp,
                                title: 'Banking & Financial Services',
                                subtitle: 'Deliver AI-Powered Intelligence',
                                description: 'AI-powered risk assessment, fraud detection, and personalized customer experiences',
                                image: '/images/ai/uc1.jfif'
                            },
                            {
                                icon: Shield,
                                title: 'Healthcare & Life Sciences',
                                subtitle: 'Impacting Healthcare with AI',
                                description: 'Enhanced diagnostics, patient care optimization, and drug discovery acceleration',
                                image: '/images/ai/uc2.jfif'
                            },
                            {
                                icon: Globe,
                                title: 'Software & Hi-Tech',
                                subtitle: 'Empowering Hi-Tech with AI',
                                description: 'Automated development workflows, intelligent testing, and predictive maintenance',
                                image: '/images/ai/uc3.jfif'
                            }
                        ].map((useCase, i) => {
                            const Icon = useCase.icon;
                            return (
                                <div
                                    key={i}
                                    className="group bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
                                    data-testid={`use-case-card-${i}`}
                                >
                                    <div
                                        className="h-56 bg-cover bg-center relative"
                                        style={{ backgroundImage: `url(${useCase.image})` }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e] to-transparent" />
                                        <div className="absolute top-4 right-4 p-2 bg-purple-600/30 backdrop-blur-sm rounded-lg border border-purple-500/30">
                                            <Icon className="text-purple-300 w-5 h-5" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                    <div className="p-8">
                                        <div className="text-purple-400 text-sm font-semibold mb-2">{useCase.subtitle}</div>
                                        <h3 className="font-bold text-2xl mb-3 text-white group-hover:text-purple-300 transition-colors">
                                            {useCase.title}
                                        </h3>
                                        <p className="text-gray-400 leading-relaxed mb-4">
                                            {useCase.description}
                                        </p>
                                        <div className="flex items-center text-purple-400 font-semibold text-sm group-hover:gap-2 transition-all">
                                            Learn More
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TECHSPR.AI Advantage */}
            <section className="py-20 px-6 bg-[#0f0f10]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 text-white">TechSPR.AI Advantage</h2>
                        <p className="text-gray-400 text-lg">We work with premier brands across banking, financial services, insurance, healthcare, life sciences, and software</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Database,
                                title: 'Rich Data Legacy of 35+ Years',
                                description: 'Our company\'s beginnings are rooted in data systems. With more than 35+ years of enterprise experience, we are the ideal partner for companies looking to utilize AI.'
                            },
                            {
                                icon: Code,
                                title: 'Digital Engineering Leadership',
                                description: 'Software is core to our business and Generative AI allows us to extend our unmatched software engineering leadership into new AI engagements.'
                            },
                            {
                                icon: Sparkles,
                                title: 'IP, Accelerators, and Investments',
                                description: 'We\'re ambitious about the efficiencies, outcomes and value that AI can generate. It\'s reflected in our ongoing IP investments and accelerators.'
                            },
                            {
                                icon: Lightbulb,
                                title: 'Deep Domain Knowledge',
                                description: 'We work with premier brands across financial services, life sciences, and software. We apply our industry expertise to develop AI use cases.'
                            },
                            {
                                icon: Users,
                                title: 'Diverse AI-Ready Talent',
                                description: 'Our AI solutions are backed by 16,000+ AI-trained technical and sales professionals recognized as leaders in talent readiness.'
                            },
                            {
                                icon: Target,
                                title: 'Ecosystem Orchestrator of Choice',
                                description: 'We are a partnership-driven company with key ecosystem partners across a variety of AI platforms and industries.'
                            }
                        ].map((advantage, i) => {
                            const Icon = advantage.icon;
                            return (
                                <div
                                    key={i}
                                    className="bg-gradient-to-br from-white/5 to-white/0 hover:from-purple-600/10 hover:to-purple-800/5 p-8 rounded-xl border border-white/10 hover:border-purple-500/30 transition-all duration-300"
                                    data-testid={`advantage-card-${i}`}
                                >
                                    <div className="mb-6 p-4 bg-purple-600/20 rounded-xl w-fit">
                                        <Icon className="text-purple-400 w-8 h-8" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-bold text-lg mb-4 text-white">
                                        {advantage.title}
                                    </h3>
                                    <p className="text-gray-400 leading-relaxed text-sm">
                                        {advantage.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Awards and Recognition */}
            <section className="py-20 px-6 bg-[#1a1a2e]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 text-white">Awards and Recognitions</h2>
                        <p className="text-gray-400 text-lg">Analysts, advisors, and industry associations consistently cite our market and technical excellence</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                title: 'Named a Leader in 2024 ISG Provider Lens™',
                                subtitle: 'Advanced Analytics and AI Services (U.S.)',
                                image: '/images/ai/aw1.jfif'
                            },
                            {
                                title: 'Market Leader in HFS Horizons',
                                subtitle: 'Generative Enterprise 2024 Report',
                                image: '/images/ai/aw2.jfif'
                            },
                            {
                                title: 'Leader in 2024 ISG Provider Lens™',
                                subtitle: 'Generative AI Services (Global)',
                                image: '/images/ai/aw3.jfif'
                            }
                        ].map((award, i) => (
                            <div
                                key={i}
                                className="group bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
                                data-testid={`award-card-${i}`}
                            >
                                <div
                                    className="h-48 bg-cover bg-center relative"
                                    style={{ backgroundImage: `url(${award.image})` }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e] via-[#1a1a2e]/60 to-transparent" />
                                    <div className="absolute top-4 left-4 p-3 bg-purple-600/30 backdrop-blur-sm rounded-lg border border-purple-500/30">
                                        <Award className="text-purple-300 w-6 h-6" strokeWidth={1.5} />
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-bold text-lg mb-2 text-white group-hover:text-purple-300 transition-colors">
                                        {award.title}
                                    </h3>
                                    <p className="text-gray-400 text-sm mb-4">{award.subtitle}</p>
                                    <div className="flex items-center text-purple-400 font-semibold text-sm">
                                        Read the Report
                                        <ArrowRight className="w-4 h-4 ml-1" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Client Stories */}
            <section
                className="py-20 px-6 relative overflow-hidden"
                style={{
                    backgroundImage: 'url(/images/ai/clientstories.jfif)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-[#0f0f10]/95" />
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4 text-white">TechSPR.AI Client Stories</h2>
                        <p className="text-gray-300 text-lg">We collaborate every day with leading brands and global enterprises for AI success</p>
                    </div>
                    <div className="flex justify-center">
                        <button className="px-10 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/30 flex items-center gap-2">
                            Read Our AI Client Success Stories
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </section>

            {/* AI-Powered Digital Engineering */}
            <section className="py-20 px-6 bg-[#0f0f10]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 text-white">AI-Powered Digital Engineering and Enterprise Modernization</h2>
                        <p className="text-gray-400 text-lg">Tap into lifecycle AI capabilities to elevate customer experiences and discover new growth opportunities</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: CheckCircle2,
                                title: 'Enterprise AI Readiness',
                                description: 'Success with AI starts with successful data strategies',
                                cta: 'Start your AI readiness journey',
                                image: '/images/ai/cs1.jpeg'
                            },
                            {
                                icon: Zap,
                                title: 'Intelligent Automation',
                                description: 'Use AI to accelerate routine tasks and improve productivity',
                                cta: 'Automate your enterprise',
                                image: '/images/ai/cs2.jpeg'
                            },
                            {
                                icon: Globe,
                                title: 'Cloud & Infrastructure',
                                description: 'Scale your AI capabilities with flexible cloud strategies',
                                cta: 'Build your cloud for AI success',
                                image: '/images/ai/cs3.jpeg'
                            },
                            {
                                icon: Rocket,
                                title: 'Software Product Engineering',
                                description: 'Put AI at the center of software and product development',
                                cta: 'Rethink your software portfolio',
                                image: '/images/ai/cs4.jfif'
                            },
                            {
                                icon: Code,
                                title: 'Application Management',
                                description: 'Bring AI capabilities to your applications and digital services',
                                cta: 'Modernize your enterprise apps',
                                image: '/images/ai/cs5.jfif'
                            },
                            {
                                icon: Lock,
                                title: 'Enterprise AI Security',
                                description: 'Reduce risk and prioritize Responsible AI in your company',
                                cta: 'Build out your security profile',
                                image: '/images/ai/cs6.jfif'
                            }
                        ].map((service, i) => {
                            const Icon = service.icon;
                            return (
                                <div
                                    key={i}
                                    className="group bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300"
                                    data-testid={`engineering-service-${i}`}
                                >
                                    <div
                                        className="h-40 bg-cover bg-center relative"
                                        style={{ backgroundImage: `url(${service.image})` }}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] to-transparent" />
                                    </div>
                                    <div className="p-6">
                                        <div className="mb-4 p-2 bg-purple-600/20 rounded-lg w-fit">
                                            <Icon className="text-purple-400 w-6 h-6" strokeWidth={1.5} />
                                        </div>
                                        <h3 className="font-bold text-xl mb-3 text-white group-hover:text-purple-300 transition-colors">
                                            {service.title}
                                        </h3>
                                        <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                                            {service.description}
                                        </p>
                                        <div className="flex items-center text-purple-400 font-semibold text-sm">
                                            {service.cta}
                                            <ArrowRight className="w-4 h-4 ml-1" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Partners */}
            <section className="py-16 px-6 bg-[#1a1a2e]">
                <div className="max-w-7xl mx-auto">

                    {/* Heading */}
                    <div className="text-center mb-12">
                        <h3 className="text-2xl font-bold mb-2 text-white">
                            TechSPR.AI Partners
                        </h3>
                        <p className="text-gray-400">
                            With a dedicated hyperscalers task force, we collaborate with industry&apos;s leading platform partners
                        </p>
                    </div>

                    {/* Partner Logos */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 items-center">
                        {[
                            { name: "Microsoft", logo: "microsoft" },
                            { name: "Google Cloud", logo: "google" },
                            { name: "IBM", logo: "ibm" },
                            { name: "AWS", logo: "aws" },
                            { name: "Salesforce", logo: "salesforce" },
                            { name: "ChatGPT", logo: "chatgpt" },
                            { name: "Databricks", logo: "databricks" },
                            { name: "EmergentAI", logo: "emergentai" }
                        ].map((partner, i) => (
                            <div
                                key={i}
                                className="bg-white/5 hover:bg-white/10 p-6 rounded-lg flex items-center justify-center border border-white/10 hover:border-purple-500/30 transition-all duration-300 h-24"
                                data-testid={`partner-${i}`}
                            >
                                <img
                                    src={`/images/partners/ai/${partner.logo}.png`}
                                    alt={partner.name}
                                    className="h-8 w-auto object-contain opacity-90 hover:opacity-100 transition"
                                    loading="lazy"
                                />
                            </div>
                        ))}
                    </div>

                </div>
            </section>


            {/* FastStart CTA */}
            <section
                className="py-20 px-6 relative overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #8b5cf6 100%)'
                }}
            >
                <div
                    className="absolute inset-0 opacity-20"
                    style={{
                        backgroundImage: 'url(/images/ai/faststart.jfif)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    }}
                />
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-5xl font-bold mb-6 text-white">
                        FastStart Your AI Journey
                    </h2>
                    <p className="text-xl text-purple-100 mb-10">
                        Begin your AI transformation with our proven methodologies and expert guidance
                    </p>
                    <button className="px-12 py-5 bg-white text-purple-600 hover:bg-gray-100 font-bold rounded-lg transition-all duration-300 shadow-xl text-lg">
                        Learn more
                    </button>
                </div>
            </section>

            {/* Related Content */}
            <section className="py-20 px-6 bg-[#0f0f10]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 text-white">Related Content</h2>
                        <p className="text-gray-400 text-lg">Latest insights, case studies, and thought leadership</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                type: 'Client Success',
                                title: 'Smarter Search Starts Here',
                                description: 'How we Rewired Enterprise Knowledge with GenAI',
                                image: '/images/ai/aw1.jpeg'
                            },
                            {
                                type: 'Blog',
                                title: 'Securing GenAI Applications',
                                description: 'The Role of Enterprise-Grade LLM Firewall',
                                image: '/images/ai/aw2.jpeg'
                            },
                            {
                                type: 'Client Success',
                                title: 'From AI Chaos to Controlled Velocity',
                                description: 'How One Wealth Leader Scaled GenAI Responsibly',
                                image: '/images/ai/aw3.jpeg'
                            },
                            {
                                type: 'Whitepaper',
                                title: 'Building the Foundation',
                                description: 'For Scalable, Responsible AI',
                                image: '/images/ai/aw4.jfif'
                            }
                        ].map((content, i) => (
                            <div
                                key={i}
                                className="group bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
                                data-testid={`content-card-${i}`}
                            >
                                <div
                                    className="h-48 bg-cover bg-center relative"
                                    style={{ backgroundImage: `url(${content.image})` }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] via-transparent to-transparent" />
                                    <div className="absolute top-4 left-4 px-3 py-1 bg-purple-600/80 backdrop-blur-sm rounded-full text-xs font-semibold">
                                        {content.type}
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-bold text-lg mb-2 text-white group-hover:text-purple-300 transition-colors">
                                        {content.title}
                                    </h3>
                                    <p className="text-gray-400 text-sm mb-4">{content.description}</p>
                                    <div className="flex items-center text-purple-400 font-semibold text-sm">
                                        Read More
                                        <ArrowRight className="w-4 h-4 ml-1" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Responsible AI */}
            <section className="py-16 px-6 bg-gradient-to-b from-[#0f0f10] to-[#1a1a2e] border-t border-white/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h3 className="text-2xl font-bold mb-4 text-white">Our Commitment to Responsible AI</h3>
                    <div className="w-16 h-1 bg-purple-500 mx-auto mb-6" />
                    <p className="text-gray-400 leading-relaxed">
                        AI presents tremendous opportunities, ones that must take into account the critical need to utilize AI
                        technologies responsibly, ethically, and securely. At TECHSPR, we leverage our data heritage and Digital
                        Engineering leadership to enhance our extensive AI experience. We&apos;re committed to maintaining data security,
                        privacy, governance, and transparency in all of our client engagements.
                    </p>
                </div>
            </section>

            
        </div>
    );
};

export default AIServiceLayout;